import {isObject} from 'lodash';
import {useCallback, useState, useContext, useEffect} from 'react';
import {AxiosContext} from '../components/utils/AxiosProvider';

const useGetEndpoint = (
    endpoint,
    useCache = null,
    queryParams = null,
    cache = true,
    cancel = false,
    transform = null
) => {
    const [isLoading, setIsLoading] = useState(true && !cancel);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const {client: axiosClient} = useContext(AxiosContext);

    const onDataFetched = useCallback(
        result => {
            transform ? setData(transform(result.data)) : setData(result.data);
            setError(null);
            setIsLoading(false);
        },
        [transform]
    );

    const onError = useCallback(
        error => {
            setIsLoading(false);
            setData(null);
            setError({
                endpoint: endpoint,
                message: error?.response?.data?.message,
            });
        },
        [endpoint]
    );

    const fetchData = useCallback(() => {
        if (axiosClient && !cancel) {
            setIsLoading(true);
            axiosClient
                .get(endpoint, {
                    params: queryParams,
                    useCache: useCache,
                    cache: cache,
                })
                .then(onDataFetched)
                .catch(onError);
        }
    }, [
        axiosClient,
        cancel,
        endpoint,
        queryParams,
        useCache,
        cache,
        onDataFetched,
        onError,
    ]);

    useEffect(() => {
        // Update data when endoint changes
        // This happens in places like document detail when changing
        // types or ids without unmounting

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refresh: fetchData,
    };
};

const useGetAxiosClient = () => {
    return useContext(AxiosContext);
};

///////// USER /////////
const useGetUser = (useCache = false) => {
    return useGetEndpoint('/api/1/user', useCache, null, false);
};

const useGetProfile = (useCache = true) => {
    return useGetEndpoint('/api/1/user/profile', useCache);
};

const useGetProfileAdmin = (useCache = true, id) => {
    return useGetEndpoint(`/api/1/admin/user/${id}/profile`, useCache);
};

const useGetUserAllFormsSummary = (useCache = true) => {
    return useGetEndpoint('/api/1/user/all_forms/summary', useCache);
};

const useGetAllDocumentsSummary = (useCache = true) => {
    return useGetEndpoint('/api/1/user/all_documents/summary', useCache);
};

const useGetUserSeaservicesSummary = (useCache = true) => {
    return useGetEndpoint('/api/1/user/seaservices/summary', useCache);
};

const useGetUserEndorsements = (useCache = true) => {
    return useGetEndpoint('/api/1/user/endorsements', useCache);
};

const useGetUserForm = (type, id, useCache = true) => {
    return useGetEndpoint(`/api/1/user/${type}/${id}`, useCache);
};

const useGetApplicationChecklist = (useCache = true) => {
    return useGetEndpoint('/api/1/user/application_checklist', useCache);
};

const useGetExpirationDates = (useCache = true) => {
    return useGetEndpoint('/api/1/user/expiration_dates', useCache);
};

const useGetActiveApplication = (useCache = true) => {
    return useGetEndpoint('/api/1/user/application', useCache);
}

const API_MMLD = '/api/1/user/get_mmld_all';
const useGetMMLDPassive = (useCache = true) => {
    return useGetEndpoint(API_MMLD, useCache);
};

const useGetAdminMMLDPassive = (id, useCache = true) => {
    return useGetEndpoint(`/api/1/admin/get_mmld_all/${id}`, useCache);
};

const useGetBin = (useCache = false) => {
    return useGetEndpoint('/api/1/user/bin/last', useCache);
};

///////// ADMIN /////////
const useGetSwagger = (useCache = true) => {
    return useGetEndpoint('/api/1/swagger.json', useCache);
};

const useGetAdminVersion = (useCache = true) => {
    return useGetEndpoint('/api/1/admin/version', useCache);
};

const useGetAdminTestUsers = (useCache = true) => {
    return useGetEndpoint('/api/1/admin/testusers', useCache);
};

const useGetAdminUsers = (useCache = true) => {
    return useGetEndpoint('/api/1/admin/users', useCache);
};

const useGetAdminUser = (user_id, useCache = true) => {
    return useGetEndpoint(
        `/api/1/admin/user/${user_id}`,
        useCache,
        null,
        true,
        !user_id
    );
};

const useGetAllFeedback = (useCache = true) => {
    return useGetEndpoint('/api/1/admin/feedback/all', useCache);
};

const useGetAllApplications = (useCache = true) => {
    return useGetEndpoint('/api/1/admin/application/all', useCache);
};

const useGetApplication = (appId, useCache = true) => {
    return useGetEndpoint(`/api/1/admin/application/${appId}`, useCache);
};

const useGetApplicationPayment = (appId, useCache = true) => {
    return useGetEndpoint(
        `/api/1/admin/application/${appId}/payment`,
        useCache
    );
};

const useGetApplicationPdf = (appId, useCache = true) => {
    return useGetEndpoint(
        `/api/1/admin/application/${appId}/complete_application/signed_url`,
        useCache
    );
};

///////// PAYMENTS /////////
const useGetPrices = (useCache = true) => {
    return useGetEndpoint('/api/1/payments/prices', useCache);
};

const useGetBillingAddress = (useCache = true) => {
    return useGetEndpoint('/api/1/payments/billing_address', useCache);
};

const useGetPaymentToken = (useCache = false) => {
    return useGetEndpoint('/api/1/user/payment_token', useCache, null, false);
};

const useGetUserPrices = (useCached = true) => {
    return useGetEndpoint('/api/1/payments/user/prices', useCached);
};

const useGetCreditSaveCardToken = (params = null, useCache = false) => {
    return useGetEndpoint('/api/1/payments/credit_save_card', useCache, params);
};

const useGetCurrentPromos = (useCache = true) => {
    return useGetEndpoint('/api/1/payments/current_promos', useCache);
};

const useGetReferrals = (useCache = true) => {
    return useGetEndpoint('/api/1/payments/user/referrals', useCache);
};

///////// COURSES /////////
const useGetCoursesAll = (useCache = true) => {
    // return useGetEndpoint('/api/1/courses/all', useCache, null, false);
    return useGetEndpoint('/api/1/courses/all', useCache);
};

const useGetSchoolsAll = (useCache = true) => {
    return useGetEndpoint('/api/1/schools/all', useCache);
};

const useGetNVIC = (useCache = true) => {
    return useGetEndpoint('/api/1/nvics/all', useCache);
};

///////// TANKERMAN /////////
const useGetTankermans = (useCache = true) => {
    return useGetEndpoint('/api/1/user/tankerman/all', useCache);
};

const useGetTankermanSummary = (useCache = true) => {
    return useGetEndpoint('/api/1/user/tankerman/summary', useCache);
};
///////// PORTAL /////////
const useGetPortalAllUsers = (useCache = true) => {
    return useGetEndpoint('/api/1/portal/users', useCache);
};

const useGetOrgUsers = (orgId, useCache = true) => {
    return useGetEndpoint(
        `/api/1/portal/organization/${orgId}/users`,
        useCache
    );
};

const useGetOrgUsersSummary = (orgId, useCache = true) => {
    return useGetEndpoint(
        `/api/1/portal/organization/${orgId}/users/summary`,
        useCache
    );
};

const useGetOrgMeta = (orgId, useCache = true) => {
    return useGetEndpoint(`/api/1/portal/organization/${orgId}`, useCache);
};

const useGetPortalOrgs = (useCache = true) => {
    return useGetEndpoint('/api/1/portal/organizations', useCache);
};

const useGetPortalOrgUser = (orgId, userId, useCache = true) => {
    return useGetEndpoint(
        `/api/1/portal/organization/${orgId}/user/${userId}`,
        useCache
    );
};

///////// VESSEL /////////
const cleanVessel = data => {
    if (isObject(data)) {
        const {
            grt_tonnage,
            tonnage_type,
            uscg_official_number,
            imo_number,
            mmsi,
            ...rest
        } = data;
        const newData = {
            ...rest,
            tonnage: grt_tonnage,
            tonnage_type: tonnage_type || 'grt',
            uscg_official_number: `${parseInt(uscg_official_number)}` || null,
            imo_number: parseInt(imo_number) || null,
            mmsi: parseInt(mmsi) || null,
        };
        return newData;
    } else return {};
};

const useGetVessel = (vesselId, useCache = true) => {
    return useGetEndpoint(
        `/api/1/vessels/${vesselId}`,
        useCache,
        null,
        true,
        !vesselId,
        cleanVessel
    );
};

const cleanUserVessels = data => {
    if (isObject(data)) {
        return data.user_vessels?.map(vessel => cleanVessel(vessel));
    } else return [];
};
const useGetUserVessels = (useCache = true) => {
    return useGetEndpoint(
        `/api/1/vessels/user_vessels`,
        useCache,
        null,
        true,
        null,
        cleanUserVessels
    );
};

export default useGetEndpoint;
export {
    useGetAxiosClient,
    useGetUser,
    useGetUserAllFormsSummary,
    useGetUserSeaservicesSummary,
    useGetUserEndorsements,
    useGetUserForm,
    useGetSwagger,
    useGetAdminVersion,
    useGetAdminTestUsers,
    useGetAdminUsers,
    useGetProfile,
    useGetProfileAdmin,
    useGetAllFeedback,
    useGetAllDocumentsSummary,
    useGetPrices,
    useGetApplicationChecklist,
    useGetBillingAddress,
    useGetPaymentToken,
    useGetCreditSaveCardToken,
    useGetExpirationDates,
    useGetCurrentPromos,
    useGetCoursesAll,
    useGetSchoolsAll,
    useGetActiveApplication,
    useGetMMLDPassive,
    useGetAdminMMLDPassive,
    useGetNVIC,
    useGetReferrals,
    useGetOrgUsers,
    useGetOrgMeta,
    useGetBin,
    useGetUserPrices,
    useGetOrgUsersSummary,
    useGetPortalOrgs,
    useGetPortalOrgUser,
    useGetPortalAllUsers,
    useGetVessel,
    cleanVessel,
    useGetUserVessels,
    API_MMLD,
    useGetTankermans,
    useGetTankermanSummary,
    useGetAllApplications,
    useGetApplication,
    useGetApplicationPayment,
    useGetApplicationPdf,
    useGetAdminUser,
};
